import type { PickupKind } from "../generation/types";
import { PALETTE } from "./palettes";

const SPRITE_SIZE = 64;

export class PickupSpriteGenerator {
  private readonly cache = new Map<PickupKind, HTMLCanvasElement>();

  public get(kind: PickupKind): HTMLCanvasElement {
    const cached = this.cache.get(kind);
    if (cached) return cached;
    const canvas = document.createElement("canvas");
    canvas.width = SPRITE_SIZE;
    canvas.height = SPRITE_SIZE;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Canvas 2D context is unavailable");
    context.imageSmoothingEnabled = false;
    this.drawShadow(context);
    switch (kind) {
      case "health":
        this.drawMedkit(context);
        break;
      case "armor":
        this.drawArmorVest(context);
        break;
      case "bullets":
        this.drawBulletBox(context);
        break;
      case "shells":
        this.drawShells(context);
        break;
      case "shotgun":
        this.drawShotgun(context);
        break;
      case "rifle":
        this.drawShotgun(context);
        context.fillStyle = "#66755a";
        context.fillRect(18,27,24,12);
        context.fillStyle = "#26352a";
        context.fillRect(30,38,8,16);
        context.fillStyle = "#b4bf9a";
        context.fillRect(44,23,17,4);
        break;
      case "rifleAmmo":
        this.drawBulletBox(context);
        context.fillStyle = "#98b46b";
        context.fillRect(12,37,40,14);
        context.fillStyle = "#23311d";
        context.font = "bold 10px monospace";
        context.fillText("30",26,48);
        break;
    }
    this.cache.set(kind, canvas);
    return canvas;
  }

  private drawShadow(context: CanvasRenderingContext2D): void {
    context.fillStyle = "rgba(0,0,0,.45)";
    context.fillRect(10, 57, 44, 3);
    context.fillRect(17, 60, 30, 2);
  }

  private drawMedkit(context: CanvasRenderingContext2D): void {
    context.fillStyle = PALETTE.black;
    context.fillRect(7, 19, 50, 38);
    context.fillRect(21, 12, 22, 9);
    context.fillStyle = PALETTE.steel;
    context.fillRect(24, 14, 16, 7);
    context.fillStyle = PALETTE.bone;
    context.fillRect(10, 22, 44, 32);
    context.fillStyle = PALETTE.paleSteel;
    context.fillRect(10, 22, 44, 6);
    context.fillStyle = PALETTE.red;
    context.fillRect(27, 29, 10, 20);
    context.fillRect(22, 34, 20, 10);
  }

  private drawArmorVest(context: CanvasRenderingContext2D): void {
    context.fillStyle = PALETTE.black;
    this.fillPolygon(context, [10, 21, 22, 10, 42, 10, 54, 21, 49, 58, 15, 58]);
    context.fillStyle = PALETTE.cyan;
    this.fillPolygon(context, [14, 22, 24, 14, 40, 14, 50, 22, 46, 54, 18, 54]);
    context.fillStyle = PALETTE.black;
    this.fillPolygon(context, [25, 14, 39, 14, 36, 27, 28, 27]);
    context.fillStyle = PALETTE.steel;
    context.fillRect(21, 29, 22, 21);
    context.fillStyle = PALETTE.paleSteel;
    context.fillRect(24, 32, 16, 5);
    context.fillStyle = PALETTE.iron;
    context.fillRect(29, 38, 6, 12);
  }

  private drawBulletBox(context: CanvasRenderingContext2D): void {
    context.fillStyle = PALETTE.black;
    context.fillRect(7, 28, 50, 29);
    context.fillStyle = PALETTE.moss;
    context.fillRect(10, 31, 44, 23);
    context.fillStyle = PALETTE.steel;
    context.fillRect(10, 31, 44, 5);
    for (let x = 13; x <= 45; x += 11) {
      context.fillStyle = PALETTE.black;
      context.fillRect(x, 11, 8, 23);
      context.fillStyle = PALETTE.amber;
      context.fillRect(x + 2, 14, 4, 17);
      context.fillStyle = PALETTE.bone;
      context.fillRect(x + 3, 11, 2, 4);
    }
    context.fillStyle = PALETTE.amber;
    context.fillRect(18, 40, 28, 4);
    context.fillRect(25, 47, 14, 3);
  }

  private drawShells(context: CanvasRenderingContext2D): void {
    for (let x = 8; x <= 44; x += 12) {
      context.fillStyle = PALETTE.black;
      context.fillRect(x, 14, 11, 43);
      context.fillStyle = PALETTE.red;
      context.fillRect(x + 2, 17, 7, 31);
      context.fillStyle = PALETTE.amber;
      context.fillRect(x + 2, 48, 7, 6);
      context.fillStyle = PALETTE.bone;
      context.fillRect(x + 3, 14, 5, 4);
    }
  }

  private drawShotgun(context: CanvasRenderingContext2D): void {
    context.fillStyle = PALETTE.black;
    this.fillPolygon(context, [3, 30, 43, 20, 61, 23, 61, 32, 38, 35, 17, 50, 4, 47]);
    context.fillStyle = PALETTE.paleSteel;
    this.fillPolygon(context, [26, 27, 59, 25, 59, 30, 24, 33]);
    context.fillStyle = PALETTE.steel;
    context.fillRect(20, 29, 23, 10);
    context.fillStyle = PALETTE.rust;
    context.fillRect(12, 34, 23, 9);
    context.fillStyle = PALETTE.darkRust;
    this.fillPolygon(context, [6, 39, 22, 39, 16, 48, 6, 45]);
    context.fillStyle = PALETTE.amber;
    context.fillRect(38, 32, 5, 4);
  }

  private fillPolygon(context: CanvasRenderingContext2D, coordinates: number[]): void {
    const [firstX, firstY, ...remaining] = coordinates;
    if (firstX === undefined || firstY === undefined) return;
    context.beginPath();
    context.moveTo(firstX, firstY);
    for (let index = 0; index < remaining.length; index += 2) {
      const x = remaining[index];
      const y = remaining[index + 1];
      if (x !== undefined && y !== undefined) context.lineTo(x, y);
    }
    context.closePath();
    context.fill();
  }
}
